import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, HelpCircle, Edit2, Tag, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_Q = {
  type_key: "",
  question_text: "",
  correct_answer: "",
  wrong_answers: ["", "", ""],
  scope: "cocktail",
  cocktail_id: "",
  deck_name: "",
};

/**
 * Custom questions manager. Rendered under Library → Questions.
 *
 * Also usable from the cocktail-edit dialog (via `defaultCocktailId` prop) —
 * in that mode it pre-filters the list to the current cocktail and pre-fills
 * new questions with cocktail scope.
 */
export default function QuestionsSection({ defaultCocktailId = null, embedded = false }) {
  const [types, setTypes] = useState([]);
  const [disabledAuto, setDisabledAuto] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [cocktails, setCocktails] = useState([]);
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterScope, setFilterScope] = useState(defaultCocktailId ? "cocktail" : "all");
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showTypes, setShowTypes] = useState(false);
  const [newTypeLabel, setNewTypeLabel] = useState("");

  const cocktailMap = useMemo(() => Object.fromEntries(cocktails.map(c => [c.id, c])), [cocktails]);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, qRes, cRes] = await Promise.all([
        axios.get(`${API}/question-types`),
        axios.get(`${API}/custom-questions${defaultCocktailId ? `?cocktail_id=${defaultCocktailId}` : ""}`),
        axios.get(`${API}/cocktails`),
      ]);
      setTypes(tRes.data.types || []);
      setDisabledAuto(tRes.data.disabled_auto_types || []);
      setQuestions(qRes.data || []);
      setCocktails(cRes.data || []);
      const uniqDecks = Array.from(new Set((cRes.data || []).map(c => c.category).filter(Boolean))).sort();
      setDecks(uniqDecks);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load questions");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [defaultCocktailId]);

  const typeLabel = (key) => types.find(t => t.key === key)?.label || key;

  const toggleAuto = async (key) => {
    const next = disabledAuto.includes(key) ? disabledAuto.filter(k => k !== key) : [...disabledAuto, key];
    try {
      const res = await axios.put(`${API}/question-types/disabled-auto`, { disabled_auto_types: next });
      setDisabledAuto(res.data.disabled_auto_types || []);
      toast.success(disabledAuto.includes(key) ? `${typeLabel(key)} enabled` : `${typeLabel(key)} disabled`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const addType = async () => {
    if (!newTypeLabel.trim()) return;
    try {
      const res = await axios.post(`${API}/question-types`, { label: newTypeLabel.trim() });
      setTypes(prev => [...prev, res.data]);
      setNewTypeLabel("");
      toast.success(`Added "${res.data.label}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add type");
    }
  };

  const removeType = async (key) => {
    if (!window.confirm(`Delete the "${typeLabel(key)}" type? Any questions using it will also be removed.`)) return;
    try {
      await axios.delete(`${API}/question-types/${key}`);
      setTypes(prev => prev.filter(t => t.key !== key));
      setQuestions(prev => prev.filter(q => q.type_key !== key));
      toast.success("Type removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  const openNew = () => {
    setEditing({
      ...EMPTY_Q,
      type_key: types.find(t => !t.is_auto)?.key || "",
      scope: defaultCocktailId ? "cocktail" : "cocktail",
      cocktail_id: defaultCocktailId || (cocktails[0]?.id || ""),
    });
    setShowEditor(true);
  };

  const openEdit = (q) => {
    setEditing({
      id: q.id,
      type_key: q.type_key,
      question_text: q.question_text,
      correct_answer: q.correct_answer,
      wrong_answers: [...(q.wrong_answers || ["", "", ""])].concat(["", "", ""]).slice(0, 3),
      scope: q.scope || "cocktail",
      cocktail_id: q.cocktail_id || "",
      deck_name: q.deck_name || "",
    });
    setShowEditor(true);
  };

  const save = async () => {
    const q = editing;
    if (!q?.type_key) { toast.error("Choose a question type"); return; }
    if (!q.question_text.trim()) { toast.error("Question is required"); return; }
    if (!q.correct_answer.trim()) { toast.error("Correct answer is required"); return; }
    if (q.wrong_answers.some(w => !w.trim())) { toast.error("Fill in all 3 wrong answers"); return; }
    const answers = [q.correct_answer.trim().toLowerCase(), ...q.wrong_answers.map(w => w.trim().toLowerCase())];
    if (new Set(answers).size !== 4) { toast.error("All 4 answers must be different"); return; }
    if (q.scope === "cocktail" && !q.cocktail_id) { toast.error("Pick a cocktail"); return; }
    if (q.scope === "deck" && !q.deck_name) { toast.error("Pick a deck"); return; }

    const payload = {
      type_key: q.type_key,
      question_text: q.question_text.trim(),
      correct_answer: q.correct_answer.trim(),
      wrong_answers: q.wrong_answers.map(w => w.trim()),
      scope: q.scope,
      cocktail_id: q.scope === "cocktail" ? q.cocktail_id : null,
      deck_name: q.scope === "deck" ? q.deck_name : null,
    };
    try {
      if (q.id) {
        await axios.put(`${API}/custom-questions/${q.id}`, payload);
        toast.success("Question updated");
      } else {
        await axios.post(`${API}/custom-questions`, payload);
        toast.success("Question added");
      }
      setShowEditor(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (q) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await axios.delete(`${API}/custom-questions/${q.id}`);
      setQuestions(prev => prev.filter(x => x.id !== q.id));
      toast.success("Question deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const filtered = questions.filter(q => {
    if (filterType !== "all" && q.type_key !== filterType) return false;
    if (filterScope !== "all" && q.scope !== filterScope) return false;
    return true;
  });

  const autoTypes = types.filter(t => t.is_auto);
  const customTypes = types.filter(t => !t.is_auto);

  return (
    <div className="space-y-6">
      {/* Auto question types toggle */}
      {!embedded && (
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="auto-types-panel">
          <CardContent className="p-4 space-y-3">
            <div>
              <p className="text-sm font-semibold">Auto-generated question types</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Turn off any auto type you don&apos;t want in tests. Custom questions below still fire.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {autoTypes.map(t => {
                const enabled = !disabledAuto.includes(t.key);
                return (
                  <div key={t.key} className="flex items-center justify-between rounded-md bg-[hsl(var(--secondary))]/50 px-3 py-2" data-testid={`auto-toggle-${t.key}`}>
                    <span className="text-sm font-medium">{t.label}</span>
                    <Switch checked={enabled} onCheckedChange={() => toggleAuto(t.key)} data-testid={`auto-switch-${t.key}`} />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Custom types manager */}
      {!embedded && (
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="custom-types-panel">
          <CardContent className="p-4 space-y-3">
            <button className="w-full flex items-center justify-between text-left" onClick={() => setShowTypes(v => !v)} data-testid="toggle-types-panel">
              <div>
                <p className="text-sm font-semibold flex items-center gap-2"><Tag size={14} className="text-[hsl(var(--primary))]" /> Custom question types</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">{customTypes.length} type{customTypes.length === 1 ? "" : "s"}. Add types like History, Flavour, Method.</p>
              </div>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">{showTypes ? "Hide" : "Manage"}</span>
            </button>
            {showTypes && (
              <>
                <div className="flex flex-wrap gap-2">
                  {customTypes.length === 0 && (
                    <p className="text-xs italic text-[hsl(var(--muted-foreground))]">No custom types yet.</p>
                  )}
                  {customTypes.map(t => (
                    <span key={t.key} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]" data-testid={`custom-type-${t.key}`}>
                      {t.label}
                      <button onClick={() => removeType(t.key)} className="ml-1 hover:text-red-400" aria-label={`Delete ${t.label}`} data-testid={`remove-type-${t.key}`}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newTypeLabel}
                    onChange={e => setNewTypeLabel(e.target.value)}
                    placeholder="e.g. History"
                    className="bg-[hsl(var(--secondary))] flex-1"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addType(); } }}
                    data-testid="new-type-input"
                  />
                  <Button onClick={addType} disabled={!newTypeLabel.trim()} data-testid="add-type-btn">
                    <Plus size={14} className="mr-1" /> Add type
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters + add */}
      <div className="flex flex-wrap items-center gap-2">
        {!embedded && (
          <>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-9 w-[160px] bg-[hsl(var(--secondary))] text-xs" data-testid="filter-type">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {types.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterScope} onValueChange={setFilterScope}>
              <SelectTrigger className="h-9 w-[160px] bg-[hsl(var(--secondary))] text-xs" data-testid="filter-scope">
                <SelectValue placeholder="All scopes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All scopes</SelectItem>
                <SelectItem value="cocktail">Cocktail-specific</SelectItem>
                <SelectItem value="deck">Deck-wide</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
        <div className="ml-auto">
          <Button onClick={openNew} data-testid="add-question-btn"><Plus size={14} className="mr-1" /> Add question</Button>
        </div>
      </div>

      {/* Question list */}
      {loading ? (
        <p className="text-center text-[hsl(var(--muted-foreground))] py-8">Loading...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-14 space-y-3 rounded-lg border border-dashed border-[hsl(var(--border))]">
          <HelpCircle size={36} className="mx-auto text-[hsl(var(--muted-foreground))]" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">No custom questions yet.</p>
          <Button variant="outline" onClick={openNew}><Plus size={14} className="mr-1" /> Add your first question</Button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="questions-list">
          {filtered.map(q => (
            <div key={q.id} className="flex items-start gap-3 rounded-md bg-[hsl(var(--secondary))]/40 border border-[hsl(var(--border))] px-3 py-2" data-testid={`question-row-${q.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-1.5 mb-1">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]">{typeLabel(q.type_key)}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[hsl(var(--muted))]/30 text-[hsl(var(--muted-foreground))]">
                    {q.scope === "cocktail" ? (cocktailMap[q.cocktail_id]?.name || "Cocktail") : `Deck: ${q.deck_name}`}
                  </span>
                </div>
                <p className="text-sm font-medium">{q.question_text}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                  <span className="text-green-400 font-medium">✓ {q.correct_answer}</span>
                  {q.wrong_answers?.length ? <>  ·  <span>{q.wrong_answers.join(" · ")}</span></> : null}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => openEdit(q)} className="h-8 w-8" data-testid={`edit-question-${q.id}`}>
                  <Edit2 size={14} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(q)} className="h-8 w-8 text-red-400 hover:text-red-300" data-testid={`delete-question-${q.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit question" : "New question"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Type</Label>
                  <Select value={editing.type_key} onValueChange={(v) => setEditing(p => ({ ...p, type_key: v }))}>
                    <SelectTrigger className="bg-[hsl(var(--secondary))]" data-testid="edit-type-select"><SelectValue placeholder="Choose a type" /></SelectTrigger>
                    <SelectContent>
                      {types.map(t => <SelectItem key={t.key} value={t.key}>{t.label}{t.is_auto ? " (auto)" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Scope</Label>
                  <Select value={editing.scope} onValueChange={(v) => setEditing(p => ({ ...p, scope: v }))}>
                    <SelectTrigger className="bg-[hsl(var(--secondary))]" data-testid="edit-scope-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cocktail">Cocktail-specific</SelectItem>
                      <SelectItem value="deck">Deck-wide</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {editing.scope === "cocktail" ? (
                <div className="space-y-1.5">
                  <Label>Cocktail</Label>
                  <Select value={editing.cocktail_id} onValueChange={(v) => setEditing(p => ({ ...p, cocktail_id: v }))}>
                    <SelectTrigger className="bg-[hsl(var(--secondary))]" data-testid="edit-cocktail-select"><SelectValue placeholder="Pick a cocktail" /></SelectTrigger>
                    <SelectContent>
                      {cocktails.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label>Deck</Label>
                  <Select value={editing.deck_name} onValueChange={(v) => setEditing(p => ({ ...p, deck_name: v }))}>
                    <SelectTrigger className="bg-[hsl(var(--secondary))]" data-testid="edit-deck-select"><SelectValue placeholder="Pick a deck" /></SelectTrigger>
                    <SelectContent>
                      {decks.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Question</Label>
                <Textarea value={editing.question_text} onChange={e => setEditing(p => ({ ...p, question_text: e.target.value }))} className="bg-[hsl(var(--secondary))] min-h-[70px]" data-testid="edit-question-text" />
              </div>
              <div className="space-y-1.5">
                <Label>Correct answer</Label>
                <Input value={editing.correct_answer} onChange={e => setEditing(p => ({ ...p, correct_answer: e.target.value }))} className="bg-[hsl(var(--secondary))] border-green-500/30" data-testid="edit-correct-answer" />
              </div>
              <div className="space-y-1.5">
                <Label>Wrong answers (3)</Label>
                {editing.wrong_answers.map((w, i) => (
                  <Input
                    key={i}
                    value={w}
                    placeholder={`Wrong option ${i + 1}`}
                    onChange={e => setEditing(p => {
                      const next = [...p.wrong_answers];
                      next[i] = e.target.value;
                      return { ...p, wrong_answers: next };
                    })}
                    className="bg-[hsl(var(--secondary))]"
                    data-testid={`edit-wrong-${i}`}
                  />
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={save} className="flex-1" data-testid="save-question-btn">{editing.id ? "Save" : "Add question"}</Button>
                <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
